// ---------------------------------------------------------------------------
// Library structure selection lineage (G3 — 2026-07-27 design doc §2.1 U2)
//
// When the creator picks a library structure card at the U2 judgment moment,
// the chosen asset's identity is persisted with the project as
// `settings.libraryStructureSelection = { assetId, patternId, kind,
// selectedAt }` — lineage for "this project was started from that admitted
// library asset". It rides the same G1 settings birth-write/PATCH machinery
// as `settings.generation` and MERGES next to it (never clobbers siblings).
//
// Like project-generation-durability.ts, this module is pure vocabulary with
// no imports so every consumer (create leg, dashboard, future G4 fork lane)
// shares one testable definition.
// ---------------------------------------------------------------------------

export const LIBRARY_STRUCTURE_SELECTION_SETTINGS_KEY =
  'libraryStructureSelection'

export const LIBRARY_STRUCTURE_SELECTION_KINDS = [
  'actionflow-recipe',
  'contract-scenario',
] as const

export type LibraryStructureSelectionKind =
  (typeof LIBRARY_STRUCTURE_SELECTION_KINDS)[number]

export interface LibraryStructureSelection {
  /** Admitted agentic asset IRI the creator chose to start from. */
  assetId: string
  /** Stable pattern identity (the card's dedupe key + title source). */
  patternId: string
  kind: LibraryStructureSelectionKind
  /** ISO timestamp of the U2 judgment moment. */
  selectedAt: string
}

export function buildLibraryStructureSelection(
  input: {
    assetId: string
    patternId: string
    kind: LibraryStructureSelectionKind
  },
  now: Date = new Date(),
): LibraryStructureSelection {
  return {
    assetId: input.assetId,
    patternId: input.patternId,
    kind: input.kind,
    selectedAt: now.toISOString(),
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Defensive read of `settings.libraryStructureSelection`; malformed → null. */
export function readLibraryStructureSelection(
  settings: unknown,
): LibraryStructureSelection | null {
  const record = asRecord(settings)
  const selection = asRecord(
    record?.[LIBRARY_STRUCTURE_SELECTION_SETTINGS_KEY],
  )
  if (!selection) return null
  const { assetId, patternId, kind, selectedAt } = selection
  if (typeof assetId !== 'string' || !assetId) return null
  if (typeof patternId !== 'string' || !patternId) return null
  if (
    !LIBRARY_STRUCTURE_SELECTION_KINDS.includes(
      kind as LibraryStructureSelectionKind,
    )
  ) {
    return null
  }
  if (typeof selectedAt !== 'string' || !selectedAt) return null
  return {
    assetId,
    patternId,
    kind: kind as LibraryStructureSelectionKind,
    selectedAt,
  }
}

/** Merge the selection into settings without dropping sibling keys. */
export function withLibraryStructureSelection(
  settings: unknown,
  selection: LibraryStructureSelection,
): Record<string, unknown> {
  return {
    ...(asRecord(settings) ?? {}),
    [LIBRARY_STRUCTURE_SELECTION_SETTINGS_KEY]: selection,
  }
}
